// Project-level CDM audit score roll-up: average observation score + red/amber/green
// counts across all CDM audits, with a per-audit breakdown.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HardHat, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const scoreColor = (n) => (n == null ? "#9A968F" : n >= 4 ? "#2E7D32" : n >= 3 ? "#7CB342" : n >= 2 ? "#E08600" : "#C62828");

export default function CdmRollup({ projectId }) {
    const [data, setData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let live = true;
        api.get("/forms/cdm-summary", { params: { project_id: projectId } })
            .then((r) => { if (live) setData(r.data); })
            .catch(() => { });
        return () => { live = false; };
    }, [projectId]);

    if (!data || !data.audit_count) return null;
    const avg = data.avg_score;

    return (
        <div data-testid="cdm-rollup" className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }}>
            <div className="flex items-center gap-2 mb-4">
                <span className="grid place-items-center w-8 h-8 rounded-lg" style={{ background: "#FBEFE5" }}>
                    <HardHat size={16} style={{ color: "var(--brand-accent,#C75A1B)" }} />
                </span>
                <div>
                    <h3 className="text-[15px] font-bold text-[#1F1F1F]">CDM audit performance</h3>
                    <p className="text-[11.5px] text-[#9A968F]">{data.audit_count} audit{data.audit_count > 1 ? "s" : ""} · {data.scored} items scored</p>
                </div>
                <div className="ml-auto text-right">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-[#9A968F]">Avg score</div>
                    <div className="text-[26px] font-bold leading-none" style={{ color: scoreColor(avg) }} data-testid="cdm-rollup-avg">
                        {avg == null ? "—" : avg}<span className="text-[13px] text-[#9A968F] font-semibold"> / 5</span>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2 mb-4">
                {[["Unacceptable / Poor", data.red + data.amber, "#C62828", "cdm-rollup-redamber"],
                  ["Satisfactory+", data.green, "#2E7D32", "cdm-rollup-green"]].map(([label, count, color, tid]) => (
                    <div key={label} data-testid={tid} className="flex-1 rounded-xl px-3 py-2.5 border" style={{ borderColor: "#EFEBE5" }}>
                        <div className="text-[22px] font-bold leading-none" style={{ color }}>{count}</div>
                        <div className="text-[11px] text-[#6F6A64] mt-1">{label}</div>
                    </div>
                ))}
            </div>

            <div className="space-y-1.5">
                {data.audits.slice(0, 5).map((a) => (
                    <button key={a.id} data-testid={`cdm-rollup-audit-${a.id}`}
                        onClick={() => navigate(`/app/inspections/${a.id}`)}
                        className="w-full flex items-center gap-2 text-left px-2.5 py-2 rounded-lg hover:bg-[#F6F4F0]">
                        <span className="w-8 h-8 grid place-items-center rounded-lg text-[12px] font-bold text-white shrink-0"
                            style={{ background: scoreColor(a.avg_score) }}>
                            {a.avg_score == null ? "—" : a.avg_score}
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block text-[12.5px] font-semibold text-[#1F1F1F] truncate">{a.title}</span>
                            <span className="block text-[11px] text-[#9A968F]">{a.status} · {a.submitted_at} · {a.red + a.amber} red/amber</span>
                        </span>
                        <ChevronRight size={15} className="text-[#C0BCB4]" />
                    </button>
                ))}
            </div>
        </div>
    );
}
